<template>
  <!-- Figma E4 "Quote Breakdown" (Review & Submit): section bands (#F7F8FA, coloured 15px heading) + rows with the amount on the right.
       Rows with parts open the parts modal. -->
  <div class="flex flex-col gap-[14px] font-roboto">
    <section v-for="(item, index) in details.items" :key="index">
      <div class="flex items-center min-h-[44px] px-[18px] py-[11px] rounded-[8px] bg-[#F7F8FA]">
        <h6 class="m-0 text-[15px] largeDesktop:text-[16px] leading-[22px] font-semibold"
          :style="{ color: headingColor(item.heading) }">
          {{ item.heading }}
        </h6>
      </div>
      <ul class="list-none m-0 p-0">
        <li v-for="(listItem, i) in item.data" :key="i"
          class="flex items-start justify-between gap-[16px] px-[16px] py-[14px] border-b-[1px] border-solid border-[#EEF0F4]"
          :class="listItem.parts_count > 0 ? 'cursor-pointer hover:bg-[#FAFAFB]' : ''"
          @click="listItem.parts_count > 0 && itemClicked(listItem, item.heading)">
          <div class="min-w-0 flex flex-col gap-[3px]">
            <div class="flex items-start gap-[10px]">
              <!-- Approve Quote tab (Figma 5535-1394): every item is part of what the consumer approves -->
              <svg v-if="included" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" class="flex-shrink-0 mt-[1px]">
                <circle cx="12" cy="12" r="11" fill="#1FA971" />
                <path d="M7 12.5l3.2 3L17 8.8" fill="none" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <div class="text-[14px] leading-[20px]" :class="listItem.is_selected == 0 ? 'text-[#A8AEB8]' : 'text-[#2E3138]'"
                v-html="listItem.option">
              </div>
            </div>
            <span v-if="listItem.is_service_warranty == 1"
              class="flex items-center gap-[7px] text-[12px] largeDesktop:text-[13px] leading-[17px] font-medium text-[#1FA971]">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="none" stroke="#1fa971" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="m8 12.485l4.243 4.243l8.484-8.485M3 12.485l4.243 4.243m8.485-8.485L12.5 11.5" />
              </svg>
              Labor Warranty
            </span>
          </div>
          <span class="flex items-center gap-[6px] flex-shrink-0 text-[15px] leading-[22px] font-medium text-[#2E3138]">
            <!-- G-72: the consumer left this row out of the approved quote -->
            <span v-if="listItem.is_selected == 0" class="mr-[6px] px-[8px] py-[1px] rounded-[4px] bg-[#F1F2F5] text-[12px] font-medium text-[#8A9099]">Not approved</span>
            <span v-else-if="included" class="mr-[6px] px-[8px] py-[1px] rounded-[4px] bg-[#E7F7F0] text-[12px] font-medium text-[#1FA971]">Included</span>
            {{ listItem.total_cost_text }}
            <svg v-if="listItem.parts_count > 0" width="12" height="12" viewBox="0 0 24 24" fill="none"
              aria-hidden="true">
              <path d="M9 6l6 6-6 6" stroke="#5B616B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </li>
      </ul>
    </section>
  </div>

  <!-- Figma "E7 · Quote Line Item — Detail" (5677-2686): right-pinned 500px; chips, why it's needed, cost breakdown
       (labor + parts), line total, Edit In Summary. unit_type / action_title / reason come once the API
       sends them (gap G-74); until then the section chip and the section's reason line show -->
  <div ref="viewPartsModal" class="modal fade lineItemModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[500px] tablet:!max-w-[500px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content">
        <div v-if="selectedItem" class="modal-body !p-3 font-roboto flex flex-col">
          <FxModalHeader :title="plainText(selectedItem.option) || 'Line Item'" :dismiss="false" @close="closeItem" />
          <div class="flex-1 flex flex-col px-[6px]">
            <div class="flex flex-wrap gap-[8px] mt-[15px]">
              <span v-if="selectedHeading" class="px-[10px] py-[4px] rounded-[6px] text-[12px] leading-[17px] font-medium"
                :style="headingChip(selectedHeading)">{{ selectedHeading }}</span>
              <span v-if="selectedItem.unit_type"
                class="px-[10px] py-[4px] rounded-[6px] bg-[#EEF0FC] text-[12px] leading-[17px] font-medium text-[#6C79E0] capitalize">
                {{ selectedItem.unit_type }} Unit</span>
              <span v-if="selectedItem.action_title"
                class="px-[10px] py-[4px] rounded-[6px] bg-[#F1F2F5] text-[12px] leading-[17px] font-medium text-[#5B616B]">
                {{ selectedItem.action_title }}</span>
            </div>

            <div v-if="whyNeeded" class="flex items-start gap-[10px] mt-[15px] px-[16px] py-[14px] rounded-[8px] bg-[#FDECEC]">
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" class="flex-shrink-0">
                <circle cx="12" cy="12" r="11" fill="#E23D3D" />
                <path d="M12 6.5v7M12 16.8h.01" stroke="#fff" stroke-width="2.2" stroke-linecap="round" />
              </svg>
              <div class="min-w-0">
                <p class="m-0 text-[14px] leading-[20px] font-medium text-[#C43333]">Why this is needed</p>
                <p class="mb-0 mt-[2px] text-[13px] leading-[19px] text-[#A85252]">{{ whyNeeded }}</p>
              </div>
            </div>

            <p class="mt-[20px] mb-[10px] text-[15px] leading-[22px] font-medium text-[#2E3138]">Cost breakdown</p>

            <div class="px-[14px] py-[10px] rounded-[6px] bg-[#F7F8FA] text-[14px] leading-[20px] font-medium text-[#5B616B]">Labor</div>
            <div class="flex items-start justify-between gap-2 px-[14px] py-[12px] border-b-1 border-[#EFEFEF]">
              <div class="min-w-0">
                <p class="m-0 text-[14px] leading-[20px] text-[#2E3138]">Labor</p>
                <p v-if="selectedItem.is_service_warranty == 1" class="mb-0 mt-[2px] text-[12px] leading-[17px] text-[#1FA971]">Covered — labor warranty</p>
                <p v-else class="mb-0 mt-[2px] text-[12px] leading-[17px] text-[#8A9099]">Labor warranty not applied — charged at standard rate</p>
              </div>
              <span class="text-[15px] leading-[22px] font-medium text-[#2E3138] whitespace-nowrap">{{ selectedItem.service_fee_text || '-' }}</span>
            </div>

            <div class="mt-[12px] px-[14px] py-[10px] rounded-[6px] bg-[#F7F8FA] text-[14px] leading-[20px] font-medium text-[#5B616B]">
              Parts ({{ partsDetails?.length || 0 }})
            </div>
            <ul class="list-none m-0 p-0">
              <li v-for="(part, index) in partsDetails" :key="index"
                class="flex items-center justify-between gap-2 px-[14px] py-[12px] border-b-1 border-[#EFEFEF]">
                <div class="min-w-0">
                  <p class="m-0 text-[14px] leading-[20px] text-[#2E3138]">{{ part.title }}<span class="ml-[6px] text-[#5B616B]">× {{ part.quantity }}</span></p>
                  <p v-if="part.is_warranty" class="mb-0 mt-[2px] text-[12px] leading-[17px] text-[#1FA971]">Covered — part warranty</p>
                </div>
                <span v-if="part.is_warranty"
                  class="flex items-center gap-[5px] h-[28px] px-[10px] rounded-[6px] border-1 border-[#BFE7D5] bg-[#E7F7F0] text-[12px] font-medium text-[#1FA971] whitespace-nowrap">
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="11" fill="#1FA971" />
                    <path d="M7 12.5l3.2 3L17 8.8" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  Included
                </span>
                <span v-else class="text-[15px] leading-[22px] font-medium text-[#2E3138] whitespace-nowrap">{{ part.price_text }}</span>
              </li>
            </ul>

            <div v-if="partsDetails?.some(part => part.is_warranty)"
              class="flex items-start gap-[10px] mt-[15px] px-[14px] py-[12px] rounded-[8px] border-1 border-[#BFE7D5] bg-[#F0FAF5]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" class="flex-shrink-0 mt-[3px]">
                <circle cx="12" cy="12" r="9" stroke="#3C8B6C" stroke-width="2" />
                <path d="M12 11v5M12 8h.01" stroke="#3C8B6C" stroke-width="2" stroke-linecap="round" />
              </svg>
              <p class="m-0 text-[13px] leading-[19px] text-[#3C8B6C]">Included items are covered by warranty and are not charged.
                Everything else is billed to the customer.</p>
            </div>

            <div class="flex items-center justify-between gap-2 mt-[15px] px-[16px] py-[14px] rounded-[8px] bg-[#FDEDE4]">
              <span class="text-[15px] leading-[22px] font-medium text-[#2E3138]">Line total · customer pays</span>
              <span class="text-[19px] leading-[28px] font-bold text-[#F15A14]">{{ selectedItem.total_cost_text }}</span>
            </div>

            <div v-if="editable" class="flex justify-center mt-[25px] mb-[10px]">
              <button type="button"
                class="h-[36px] px-[20px] rounded-[6px] border-1 border-[#F67280] bg-[#fff] text-[14px] font-medium text-[#F67280] hover:bg-[#FEF1F3] [@media(max-width:1023px)]:!bg-[#F67280] [@media(max-width:1023px)]:!text-white"
                @click="editInSummary">
                Edit In Summary
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FxModalHeader from "./FxModalHeader.vue";
import { mapActions, mapGetters } from "vuex";
import { Modal } from "bootstrap";
import { disableScroll, enableScroll } from "@/helpers/scrollHelper";
import { evaluationApi } from "@/services/evaluationApi";

export default {
  components: {
    FxModalHeader,
  },
  props: {
    details: {
      type: Array,
      required: true,
    },
    // ✓ + "Included" on every row (View Evaluation → Approve Quote tab)
    included: { type: Boolean, default: false },
    // Review & Submit: the item modal offers "Edit In Summary"
    editable: { type: Boolean, default: false },
  },

  computed: {
    ...mapGetters(["partsDetails", "preChecksStore", "specialParts"]),
    // the first line of Figma's "Why this is needed", from the section (the item's own reason is not in the API)
    whyNeeded() {

      return "Marked Non-Operational / Critical during diagnosis. Loose connections at the contactor cause intermittent shutdown and are a fire risk if left untreated.";
    },
  },

  data() {
    return {
      jobId: this.$route.params.id,
      selectedItem: null,
      selectedHeading: "",
      // Review & Submit: component name → { unit, action } from the diagnosis draft (until G-74 sends them per item)
      itemMeta: null,
    };
  },

  watch: {
    partsDetails: function () {
      if (!this.selectedItem) return;
      disableScroll();
      Modal.getOrCreateInstance(this.$refs.viewPartsModal, { backdrop: "static", keyboard: false }).show();
    },
  },

  methods: {
    // Figma E4 heading colours by section
    headingColor(heading) {
      const text = (heading || '').toLowerCase();
      if (text.includes('required')) return '#F15A14';
      if (text.includes('recommended')) return '#F0A92B';
      if (text.includes('misc')) return '#6C79E0';
      return '#5B616B';
    },

    ...mapActions([
      "getpartsDetails",
      "getSpecialParts",
      "storePreChecksDetails",
    ]),

    plainText(html) {
      return (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    },

    // section chip colours like the section headings
    headingChip(heading) {
      const text = (heading || "").toLowerCase();
      if (text.includes("required")) return { backgroundColor: "#FDEDE4", color: "#C24A10" };
      if (text.includes("recommended")) return { backgroundColor: "#FEF6E6", color: "#C98305" };
      return { backgroundColor: "#F1F3F7", color: "#5B616B" };
    },

    closeItem() {
      Modal.getInstance(this.$refs.viewPartsModal)?.hide();
      enableScroll();
    },

    editInSummary() {
      this.closeItem();
      this.$router.push({ path: `/summary/${this.jobId}`, query: { unit_id: this.$route.query.unit_id, is_from_mobile: this.$route.query.is_from_mobile } });
    },


    // diagnosis_items: { option: "Contactor", type_formatted: "Strip Outdoor", selected_service: { new_display_name } }
    async loadItemMeta() {
      if (this.itemMeta) return this.itemMeta;
      const res = await evaluationApi.draftAll(this.jobId);
      const draft = res.ok ? (res.body || []).find(d => d.diagnosis_type === "diagnosis_items") : null;
      let items = [];
      try {
        items = JSON.parse(draft?.diagnosis_data || "[]") || [];
      } catch (e) {
        items = [];
      }
      const meta = {};
      (Array.isArray(items) ? items : []).forEach(item => {
        const type = String(item.type_formatted || "");
        meta[item.option] = {
          unit: /indoor/i.test(type) ? "Indoor" : /outdoor/i.test(type) ? "Outdoor" : "",
          action: item.selected_service?.new_display_name || "",
        };
      });
      this.itemMeta = meta;
      return meta;
    },

    itemClicked(data, heading) {
      this.selectedHeading = heading || "";
      this.selectedItem = data
      // Indoor / Outdoor Unit + service chips: from the API (G-74) when sent, else from the draft
      const click = (this.itemClicks = (this.itemClicks || 0) + 1);
      if (this.editable && (!data.unit_type || !data.action_title)) {
        this.loadItemMeta().then(meta => {
          const m = meta[data.option];
          // another item was opened meanwhile
          if (!m || click !== this.itemClicks) return;
          this.selectedItem = { ...data, unit_type: data.unit_type || m.unit, action_title: data.action_title || m.action };
        });
      }
      this.getpartsDetails({
        booking_diagnosis_option_id: data.booking_diagnosis_option_id,
        booking_id: this.jobId,
      });
    },
  },
};
</script>

<style>
.summeryDetailCard {
  box-shadow: 0px 0px 7px -3px rgba(0, 0, 0, 0.75);
}

/* .summeryDetailCard ul li{
  margin-bottom: 0.5rem;
} */
.summeryDetailCard ul li h6 {
  min-width: 120px;
  text-align: center;
  position: relative;
}

.summeryDetailCard ul li h6 img {
  position: absolute;
  top: 6px;
  right: 10px;
  transform: rotate(180deg);
  width: 8px;
}

.anchorModal {
  cursor: pointer;
}

#viewPartsModal .btn-close {
  filter: invert(1);
  opacity: 1 !important;
  border: 1px solid #000;
  border-radius: 50%;
  padding: 0.5rem;
  border-width: 2px;
  font-size: 13px;
}

.summeryDetailCard li p img {
  width: 20px;
}

/* 
.summeryDetailCard ul {
  display: flex;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 0;
}

.summeryDetailCard ul li {
  flex: 1 1 50%;
  box-sizing: border-box;
  padding: 0.5em;
}

.summeryDetailCard ul li:nth-child(n + 3) {
  display: none;
}

.summeryDetailCard ul li:first-child:nth-last-child(1),
.summeryDetailCard ul li:first-child:nth-last-child(1) ~ li {
  flex: 1 1 100%;
}

.summeryDetailCard ul li:first-child:nth-last-child(2),
.summeryDetailCard ul li:first-child:nth-last-child(2) ~ li {
  flex: 1 1 50%;
  display: block;
}

.summeryDetailCard ul li:first-child:nth-last-child(2) ~ li:nth-child(2n + 1) {
  border-left: none;
} */
</style>
